/**
 * インストーラーダウンロードコンポーネント
 *
 * 顧客詳細画面に埋め込まれるクライアントインストーラーDL UI。
 * Cloud Function API 経由でリリース一覧を取得し、
 * Firebase Storage SDK で DL URL を生成してブラウザDLを開始する。
 */

import { useState, useEffect } from "react";
import {
  Paper,
  Text,
  Group,
  Title,
  Button,
  Badge,
  Stack,
  Table,
} from "@mantine/core";
import { showError } from "../../utils/notifications";
import { getAuthInstance, getStorageInstance } from "../../lib/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { CUSTOMER_API_BASE_URL } from "../../constants/api";
import { Download, Package } from "lucide-react";

/**
 * インストーラー設定情報（最新バージョン用 — 既存互換）
 */
interface InstallerConfig {
  version: string;
  filename: string;
}

/**
 * リリース情報（バージョン履歴用）
 */
interface ReleaseInfo {
  id: string;
  version: string;
  filename: string;
  deployedAt?: string;
  commitSha?: string;
}

export function InstallerDownloadPanel() {
  const [config, setConfig] = useState<InstallerConfig | null>(null);
  const [releases, setReleases] = useState<ReleaseInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // インストーラー情報をAPI経由で取得
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const auth = getAuthInstance();
        const user = auth.currentUser;
        if (!user) return;

        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        // 既存 API（最新バージョン）と新 API（全バージョン）を並行取得
        const [configRes, releasesRes] = await Promise.all([
          fetch(`${CUSTOMER_API_BASE_URL}/installer/config`, { headers }),
          fetch(`${CUSTOMER_API_BASE_URL}/installer/releases`, { headers }).catch(() => null),
        ]);

        if (!cancelled) {
          // 最新バージョン（既存互換）
          if (configRes.ok) {
            const data = await configRes.json();
            if (data.config) setConfig(data.config);
          }

          // リリース一覧（新機能）
          if (releasesRes?.ok) {
            const data = await releasesRes.json();
            if (data.releases) setReleases(data.releases);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("インストーラー情報の取得に失敗:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    loadData();

    return () => { cancelled = true; };
  }, []);

  // ダウンロード実行
  const handleDownload = async (filename: string, version: string) => {
    setDownloadingId(version);
    try {
      const storage = getStorageInstance();
      const fileRef = ref(storage, `releases/${filename}`);
      const url = await getDownloadURL(fileRef);

      // ブラウザのダウンロードを開始
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      showError("インストーラーのダウンロードに失敗しました");
    } finally {
      setDownloadingId(null);
    }
  };

  // 日時をフォーマット
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // リリース一覧がある場合は一覧表示、なければ従来の単一表示にフォールバック
  const hasReleases = releases.length > 0;

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" align="center" mb={hasReleases ? "md" : 0}>
        <Group gap="xs">
          <Package size={18} />
          <Title order={5}>クライアントインストーラー</Title>
        </Group>

        {/* 最新バージョンバッジ */}
        {config && (
          <Badge size="sm" variant="light" color="blue">
            最新: v{config.version}
          </Badge>
        )}
      </Group>

      {loading && (
        <Text size="sm" c="dimmed" mt="xs">
          読み込み中...
        </Text>
      )}

      {!loading && !config && !hasReleases && (
        <Text size="sm" c="dimmed" mt="xs">
          インストーラーが登録されていません
        </Text>
      )}

      {/* リリース一覧テーブル */}
      {hasReleases && (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>バージョン</Table.Th>
              <Table.Th>ファイル名</Table.Th>
              <Table.Th>デプロイ日時</Table.Th>
              <Table.Th style={{ width: 120 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {releases.map((release, index) => (
              <Table.Tr key={release.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>v{release.version}</Text>
                    {index === 0 && (
                      <Badge size="xs" variant="filled" color="green">最新</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{release.filename}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{formatDate(release.deployedAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    leftSection={<Download size={14} />}
                    onClick={() => handleDownload(release.filename, release.version)}
                    loading={downloadingId === release.version}
                    variant="light"
                  >
                    DL
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* フォールバック: releasesコレクションがまだ無い場合は従来の単一表示 */}
      {!hasReleases && config && (
        <Stack gap="xs" mt="xs">
          <Group gap="sm">
            <Button
              size="xs"
              leftSection={<Download size={14} />}
              onClick={() => handleDownload(config.filename, config.version)}
              loading={downloadingId === config.version}
              variant="light"
            >
              ダウンロード
            </Button>
          </Group>
          <Text size="xs" c="dimmed">{config.filename}</Text>
        </Stack>
      )}
    </Paper>
  );
}
